import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NewPeriod, Period } from '../../../interfaces/period';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RestaurantService } from '../../../services/restaurant/restaurant.service';
import { NotifierService } from 'angular-notifier';
import { TranslateService } from '@ngx-translate/core';
import { filter, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../confirm-dialog/confirmation-dialog.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@UntilDestroy()
@Component({
  selector: 'app-brightness-schedule',
  templateUrl: './brightness-schedule.component.html',
  styleUrls: ['./brightness-schedule.component.scss']
})
export class BrightnessScheduleComponent implements OnInit {

  public restaurantId = this.data.restaurantId;
  public editable = this.data.editable;
  public isEditPeriods = false;
  public isSomePeriodEdited = false;
  public brightnessSchedule: Period[] = [];
  public loading = true;
  public enableSchedule = this.data.brightnessSchedule;


  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { restaurantId: number; editable: boolean, brightnessSchedule: boolean},
    private dialogRef: MatDialogRef<BrightnessScheduleComponent, { enabled: boolean }>,
    private dialog: MatDialog,
    private notifier: NotifierService,
    private restaurant: RestaurantService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.restaurant.getAllBrightnessPeriod(this.restaurantId).pipe(
      tap((brightnessSchedule: Period[]) => {
        this.brightnessSchedule = brightnessSchedule;
        this.loading = false;
      }),
      untilDestroyed(this),
    ).subscribe();
  }

  public createPeriod(period: NewPeriod): void {
    const newPeriod = {
      ...period,
      level: period.level ? period.level / 100 : undefined,
    };

    this.restaurant.createBrightnessPeriod(this.restaurantId, newPeriod).pipe(
      untilDestroyed(this)
    ).subscribe((response: Period) => {
      this.notifier.notify('success', this.translate.instant('period.brightness.messages.addedPeriod'));
      this.brightnessSchedule.push(response);
      this.sortPeriods();
    });
    this.sortPeriods();
  }

  public updatePeriod(period: {
    id: number;
    level?: number;
    startDay: number;
    startTime: string;
    endDay: number;
    endTime: string
  }): void {
    const updatedPeriod = {
      ...period,
      level: period.level ? period.level / 100 : undefined,
    };
    this.restaurant.updateBrightnessPeriod(this.restaurantId, updatedPeriod).pipe(
      untilDestroyed(this)
    ).subscribe((response: Period) => {
      this.notifier.notify('success', this.translate.instant('period.brightness.messages.updatePeriod'));
      this.brightnessSchedule = this.brightnessSchedule.map((p) => {
        return p.id !== response.id ? p : response;
      });
      this.sortPeriods();
    });
  }

  public deletePeriod(id: number): void {
    this.restaurant.deleteBrightnessPeriod(this.restaurantId, id).pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.notifier.notify('success', this.translate.instant('period.brightness.messages.deletedPeriod'));
      this.brightnessSchedule = this.brightnessSchedule.filter((p) => p.id !== id);
      this.sortPeriods();
    });
  }

  private sortPeriods(): void {
    this.brightnessSchedule.sort((a, b) => {

      if (a.startDay !== b.startDay) {
        return a.startDay - b.startDay;
      }
      return a.startTime.localeCompare(b.startTime);
    });
  }

  onCloseClick(): void {
    if (this.isSomePeriodEdited) {
      const title = this.translate.instant('period.unsavedPeriodTitle');
      const message = this.translate.instant('period.unsavedChanges');
      const confirmationModal = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message,
          title
        },
        panelClass: 'confirmation-modal'
      });

      confirmationModal.afterClosed().pipe(
        filter(Boolean),
      ).subscribe((): void => {
        this.isSomePeriodEdited = false;
        this.dialogRef.close({ enabled: this.enableSchedule });
      });
    } else {
      this.isSomePeriodEdited = false;
      this.dialogRef.close({ enabled: this.enableSchedule });
    }
  }

  onScheduleToggle(event: MatSlideToggleChange): void {
    this.restaurant.configureBrightnessSchedule(this.restaurantId, event.checked).pipe(
      untilDestroyed(this)
    ).subscribe();
  }


}
